import _defineProperty from "/vercel/path1/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";
import _slicedToArray from "/vercel/path1/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/slicedToArray.js";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { useState, useEffect, useCallback } from "react";
import { useStoreState } from "pullstate";
import ACRStore from "../../../utils/ACRStore";
import { isEmptyValue, hasValueChanged } from "../../../utils/formValueUtils";

/**
 * Normalize backend field values to ensure type consistency
 * Converts number arrays to string arrays to maintain consistency with component behavior
 * This prevents false change detection without altering API payload format
 */
var normalizeBackendValue = function normalizeBackendValue(value) {
  if (Array.isArray(value)) {
    // Convert all array items to strings for consistency
    return value.map(function (v) {
      return String(v);
    });
  }
  return value;
};

/**
 * Normalize all field values in field_data object
 */
var normalizeFieldData = function normalizeFieldData(fieldData) {
  var normalized = {};
  Object.entries(fieldData).forEach(function (_ref) {
    var _ref2 = _slicedToArray(_ref, 2),
      key = _ref2[0],
      value = _ref2[1];
    normalized[key] = normalizeBackendValue(value);
  });
  return normalized;
};
var useSubmissionFormData = function useSubmissionFormData(submissionDetail) {
  // Get form data from ACRStore for this specific patient and submission
  var formDataFromStore = useStoreState(ACRStore, function (s) {
    var _s$submissionFormData;
    return (_s$submissionFormData = s.submissionFormData) === null || _s$submissionFormData === void 0 || (_s$submissionFormData = _s$submissionFormData[(submissionDetail === null || submissionDetail === void 0 ? void 0 : submissionDetail.patient_id) || ""]) === null || _s$submissionFormData === void 0 ? void 0 : _s$submissionFormData[(submissionDetail === null || submissionDetail === void 0 ? void 0 : submissionDetail.id) || 0];
  });

  // Always normalize data whether from store or backend to handle old un-normalized store data
  var _useState = useState(normalizeFieldData(formDataFromStore || (submissionDetail === null || submissionDetail === void 0 ? void 0 : submissionDetail.field_data) || {})),
    formData = _useState[0],
    setFormData = _useState[1];

  // Sync local state with store whenever store data changes
  // Always normalize to handle old un-normalized store data
  useEffect(function () {
    if (formDataFromStore) {
      setFormData(normalizeFieldData(formDataFromStore));
    }
  }, [formDataFromStore]);
  var checkIfSubmissionReady = useCallback(function (formData) {
    var _submissionDetail$mis;
    if (!(submissionDetail !== null && submissionDetail !== void 0 && submissionDetail.missing_fields)) return false;
    return submissionDetail === null || submissionDetail === void 0 || (_submissionDetail$mis = submissionDetail.missing_fields) === null || _submissionDetail$mis === void 0 ? void 0 : _submissionDetail$mis.every(function (fieldName) {
      var value = formData === null || formData === void 0 ? void 0 : formData[fieldName];
      return !isEmptyValue(value);
    });
  }, [submissionDetail === null || submissionDetail === void 0 ? void 0 : submissionDetail.missing_fields]);
  var ensureStoreInitialized = function ensureStoreInitialized(store, patientId, submissionId) {
    var _store$submissionForm, _store$submissionRead, _store$submissionChan, _store$submissionChan2, _store$submissionOrig;
    if (!((_store$submissionForm = store.submissionFormData) !== null && _store$submissionForm !== void 0 && _store$submissionForm[patientId])) {
      store.submissionFormData[patientId] = {};
    }
    if (!((_store$submissionRead = store.submissionReadyStatus) !== null && _store$submissionRead !== void 0 && _store$submissionRead[patientId])) {
      store.submissionReadyStatus[patientId] = {};
    }
    if (!((_store$submissionChan = store.submissionChangedFields) !== null && _store$submissionChan !== void 0 && _store$submissionChan[patientId])) {
      store.submissionChangedFields[patientId] = {};
    }
    if (!((_store$submissionChan2 = store.submissionChangedFields) !== null && _store$submissionChan2 !== void 0 && (_store$submissionChan2 = _store$submissionChan2[patientId]) !== null && _store$submissionChan2 !== void 0 && _store$submissionChan2[submissionId])) {
      store.submissionChangedFields[patientId][submissionId] = new Set();
    }
    if (!((_store$submissionOrig = store.submissionOriginalFieldData) !== null && _store$submissionOrig !== void 0 && _store$submissionOrig[patientId])) {
      store.submissionOriginalFieldData[patientId] = {};
    }
  };

  // Sync with store and submission detail when submission changes
  useEffect(function () {
    if (!(submissionDetail !== null && submissionDetail !== void 0 && submissionDetail.patient_id) || !(submissionDetail !== null && submissionDetail !== void 0 && submissionDetail.id)) return;
    ACRStore.update(function (store) {
      var _store$submissionOrig2, _store$submissionForm2, _store$submissionForm3, _store$submissionOrig3, _Object$keys, _store$submissionForm4;
      store.currentPatientId = submissionDetail.patient_id;
      ensureStoreInitialized(store, submissionDetail.patient_id, submissionDetail.id);

      // Store original field_data if this is the first time opening this submission
      if (!((_store$submissionOrig2 = store.submissionOriginalFieldData) !== null && _store$submissionOrig2 !== void 0 && (_store$submissionOrig2 = _store$submissionOrig2[submissionDetail.patient_id]) !== null && _store$submissionOrig2 !== void 0 && _store$submissionOrig2[submissionDetail.id])) {
        // Normalize backend values to ensure type consistency (numbers → strings)
        var originalData = normalizeFieldData((submissionDetail === null || submissionDetail === void 0 ? void 0 : submissionDetail.field_data) || {});
        store.submissionOriginalFieldData[submissionDetail.patient_id][submissionDetail.id] = originalData;
      }
      var existingStoreFormData = (_store$submissionForm2 = store.submissionFormData) === null || _store$submissionForm2 === void 0 || (_store$submissionForm2 = _store$submissionForm2[submissionDetail.patient_id]) === null || _store$submissionForm2 === void 0 ? void 0 : _store$submissionForm2[submissionDetail.id];
      if (!existingStoreFormData) {
        // Initialize with field_data from backend, normalized to strings
        var initialFormData = normalizeFieldData((submissionDetail === null || submissionDetail === void 0 ? void 0 : submissionDetail.field_data) || {});
        store.submissionFormData[submissionDetail.patient_id][submissionDetail.id] = initialFormData;
      }

      // Always recalculate changedFields based on existing formData
      // Normalize data when reading from store to handle old un-normalized data
      var existingFormData = (_store$submissionForm3 = store.submissionFormData) === null || _store$submissionForm3 === void 0 || (_store$submissionForm3 = _store$submissionForm3[submissionDetail.patient_id]) === null || _store$submissionForm3 === void 0 ? void 0 : _store$submissionForm3[submissionDetail.id];
      var originalFieldData = (_store$submissionOrig3 = store.submissionOriginalFieldData) === null || _store$submissionOrig3 === void 0 || (_store$submissionOrig3 = _store$submissionOrig3[submissionDetail.patient_id]) === null || _store$submissionOrig3 === void 0 ? void 0 : _store$submissionOrig3[submissionDetail.id];
      if (existingFormData && originalFieldData && ((_Object$keys = Object.keys(existingFormData)) === null || _Object$keys === void 0 ? void 0 : _Object$keys.length) > 0) {
        // Normalize both values before comparison to handle old un-normalized store data
        var normalizedExistingFormData = normalizeFieldData(existingFormData);
        var normalizedOriginalFieldData = normalizeFieldData(originalFieldData);

        // Recalculate changedFields by comparing formData with ORIGINAL field_data
        var recalculatedChangedFields = new Set();
        Object.entries(normalizedExistingFormData).forEach(function (_ref3) {
          var _ref4 = _slicedToArray(_ref3, 2),
            fieldId = _ref4[0],
            value = _ref4[1];
          var originalValue = normalizedOriginalFieldData === null || normalizedOriginalFieldData === void 0 ? void 0 : normalizedOriginalFieldData[fieldId];
          if (hasValueChanged(originalValue, value)) {
            recalculatedChangedFields.add(fieldId);
          }
        });

        // Always update changedFields to keep it in sync with formData
        store.submissionChangedFields[submissionDetail.patient_id][submissionDetail.id] = recalculatedChangedFields;
      }

      // Calculate ready status using the actual formData from store
      var finalFormData = (_store$submissionForm4 = store.submissionFormData) === null || _store$submissionForm4 === void 0 || (_store$submissionForm4 = _store$submissionForm4[submissionDetail.patient_id]) === null || _store$submissionForm4 === void 0 ? void 0 : _store$submissionForm4[submissionDetail.id];
      var isReady = checkIfSubmissionReady(finalFormData || {});
      store.submissionReadyStatus[submissionDetail.patient_id][submissionDetail.id] = isReady;
    });
  }, [submissionDetail === null || submissionDetail === void 0 ? void 0 : submissionDetail.id, submissionDetail === null || submissionDetail === void 0 ? void 0 : submissionDetail.patient_id]);
  var handleFieldChange = useCallback(function (fieldId, value) {
    var _storeState$submissio;
    if (!(submissionDetail !== null && submissionDetail !== void 0 && submissionDetail.patient_id) || !(submissionDetail !== null && submissionDetail !== void 0 && submissionDetail.id)) {
      return;
    }
    var updatedFormData = _objectSpread(_objectSpread({}, formData), {}, _defineProperty({}, fieldId, value));
    setFormData(updatedFormData);
    var isReady = checkIfSubmissionReady(updatedFormData);

    // Get the ORIGINAL field value from store and normalize it
    var storeState = ACRStore.getRawState();
    var originalFieldData = (_storeState$submissio = storeState.submissionOriginalFieldData) === null || _storeState$submissio === void 0 || (_storeState$submissio = _storeState$submissio[submissionDetail.patient_id]) === null || _storeState$submissio === void 0 ? void 0 : _storeState$submissio[submissionDetail.id];
    // Normalize the original value to handle old un-normalized store data
    var originalValue = normalizeBackendValue(originalFieldData === null || originalFieldData === void 0 ? void 0 : originalFieldData[fieldId]);
    var hasChanged = hasValueChanged(originalValue, value);
    ACRStore.update(function (store) {
      var _store$submissionChan3, _store$fieldErrors;
      ensureStoreInitialized(store, submissionDetail.patient_id, submissionDetail.id);
      store.submissionFormData[submissionDetail.patient_id][submissionDetail.id] = updatedFormData;
      store.submissionReadyStatus[submissionDetail.patient_id][submissionDetail.id] = isReady;

      // Track changed fields
      var changedFields = (_store$submissionChan3 = store.submissionChangedFields) === null || _store$submissionChan3 === void 0 || (_store$submissionChan3 = _store$submissionChan3[submissionDetail.patient_id]) === null || _store$submissionChan3 === void 0 ? void 0 : _store$submissionChan3[submissionDetail.id];
      var newChangedFields = new Set(changedFields);
      if (hasChanged) {
        newChangedFields.add(fieldId);
      } else {
        newChangedFields["delete"](fieldId);
      }
      store.submissionChangedFields[submissionDetail.patient_id][submissionDetail.id] = newChangedFields;

      // Clear server-side error for this field when user edits it
      if ((_store$fieldErrors = store.fieldErrors) !== null && _store$fieldErrors !== void 0 && (_store$fieldErrors = _store$fieldErrors[submissionDetail.id]) !== null && _store$fieldErrors !== void 0 && _store$fieldErrors[fieldId]) {
        var _Object$keys2, _store$fieldErrors2;
        delete store.fieldErrors[submissionDetail.id][fieldId];
        // Clean up empty error maps
        if (((_Object$keys2 = Object.keys(((_store$fieldErrors2 = store.fieldErrors) === null || _store$fieldErrors2 === void 0 ? void 0 : _store$fieldErrors2[submissionDetail.id]) || {})) === null || _Object$keys2 === void 0 ? void 0 : _Object$keys2.length) === 0) {
          delete store.fieldErrors[submissionDetail.id];
        }
      }
    });
  }, [submissionDetail, formData, checkIfSubmissionReady]);
  return {
    formData: formData,
    handleFieldChange: handleFieldChange
  };
};
export default useSubmissionFormData;